import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useT } from "@/lib/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { LogOut, ExternalLink } from "lucide-react";
import { platformOrigin } from "@/lib/tenantHost";
import { NAV, SECTION_ORDER } from "./adminNav";
// crm-module BEGIN C1.01
import { navTestId } from "./adminNav";
import { normaliseRole } from "../crm/roles";
// crm-module END
// Relative on purpose: no "@" alias on the Jest side (cf. LanguageAwareField.jsx).
import { useConversations } from "../ConversationsContext";
import { adminHref } from "../adminPath";
import { useEntitlements } from "../entitlements/EntitlementsContext";

/** The route the unread pill belongs to — the inbox, and only the inbox. */
const MESSAGES_PATH = "/admin/messages";

/**
 * How many client messages are waiting, on the nav entry that leads to them.
 *
 * Nothing else in the console is a queue the practitioner is expected to drain, and a
 * reply that sits unread for a week is the one failure this whole feature exists to
 * prevent — so the count has to be visible from every page, not only from the inbox.
 *
 * NOTHING is rendered at zero: an empty pill is a claim ("there is a queue here") that
 * an idle inbox does not support, and a "0" badge trains the eye to ignore the badge.
 */
const UnreadPill = ({ count }) => {
  const t = useT("admin");
  if (!count) return null;
  return (
    <span
      data-testid="nav-messages-unread"
      // The number alone is ambiguous when read out of context by a screen reader —
      // the label says what it counts.
      aria-label={count === 1 ? t("msg.unreadOne") : t("msg.unreadMany", { n: count })}
      className="ml-auto min-w-[1.25rem] px-1.5 py-0.5 rounded-full bg-eh-ochre-deep text-eh-bg text-[10px] font-semibold text-center"
    >
      {count}
    </span>
  );
};

export const AdminSidebar = ({ brandLogo, user, onLogout, siteUrl = "/", onCloseMobile }) => {
  const t = useT("admin");
  const tc = useT("common");
  const { unreadCount } = useConversations();
  const { granted } = useEntitlements();

  // crm-module BEGIN C3.02 — helper scoping (decision 5, role matrix)
  // A CRM helper is scoped to the CRM section alone: every other top-level entry
  // (Bookings, Payments, Messages, Settings, the dashboard itself...) 403s server-side
  // for that role (`tenant_roles.resolve_actor`'s "anyone else" branch, since a helper
  // owns no tenant and is not a platform operator) and has no business occupying a
  // sidebar the helper can never open. An absent/unrecognised role fails CLOSED to
  // "helper" (`admin/crm/roles.js#normaliseRole`), same direction as everywhere else
  // that reads this field — an owner whose payload somehow lost `tenant_role` sees a
  // narrowed sidebar rather than one a genuinely narrower role could not use.
  const isOwner = normaliseRole(user?.tenant_role) === "owner";
  // An entry carrying `capability` renders only when the practice's plan includes it AND
  // it is built. A coming-soon capability has no page to open, so it has no entry either
  // (the roadmap page lists what is coming); while the map loads, no gated entry is shown.
  const visibleNav = NAV.filter(
    (n) => (!n.capability || granted(n.capability)) && (isOwner || n.to === "/admin/crm"),
  );
  // crm-module END
  // crm-module BEGIN C1.01
  // Sub-items (the CRM's six) are open while the address is inside their section, and
  // each obeys its own capability and, for `ownerOnly`, the tenant role `/auth/me` states
  // on the signed-in `user` — an absent role is a helper's (admin/crm/roles.js fails closed).
  const { pathname } = useLocation();
  const inSection = (n) => {
    const base = adminHref(n.to);
    return pathname === base || pathname.startsWith(`${base}/`);
  };
  const visibleChildren = (n) => (n.children || []).filter(
    (c) => (!c.capability || granted(c.capability)) && !(c.ownerOnly && !isOwner),
  );
  // crm-module END

  return (
    <aside className="w-64 shrink-0 bg-eh-cream border-r border-eh-border-soft flex flex-col h-full">
      <div className="px-5 py-5 border-b border-eh-border-soft flex items-center justify-between">
        <div className="flex flex-col items-center justify-center w-full">
          <div className="flex items-center justify-center h-20 w-full">
            <img src={brandLogo || "/brand/solunary-logo.png"} alt="Logo" className="max-h-20 max-w-full w-auto object-contain" />
          </div>
          <div className="mt-2 text-xs uppercase tracking-[0.18em] text-eh-muted text-center">{t("layout.dashboard")}</div>
          <div className="mt-2"><LanguageSwitcher variant="light" /></div>
        </div>
      </div>

      {/* BUG-017 — `data-lenis-prevent` is load-bearing, not decoration. lib/SmoothScroll.jsx
          runs Lenis on `window` with `allowNestedScroll` at its default (false), so Lenis
          calls preventDefault on every wheel/touch gesture and animates the WINDOW instead —
          a nested scroll container inside it is dead to the wheel. Lenis walks the composed
          path for this attribute and hands the gesture back to the browser, which is what
          makes the link list below scroll natively once AdminLayout bounds the sidebar to one
          viewport. Both the desktop sidebar and the mobile drawer render this component, so
          the drawer's own long list is covered by the same attribute. */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-lenis-prevent>
        {SECTION_ORDER.map((sec) => {
          const items = visibleNav.filter((n) => n.section === sec.key);
          if (items.length === 0) return null;
          return (
            <React.Fragment key={sec.key}>
              {sec.labelKey && (
                <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-wider text-eh-muted/60">{t(sec.labelKey)}</div>
              )}
              {items.map((n) => {
                const Icon = n.icon;
                /* crm-module BEGIN C1.01 */
                const open = !!n.children && inSection(n);
                const children = open ? visibleChildren(n) : [];
                /* crm-module END */
                return (
                  <React.Fragment key={n.to}>
                  <NavLink
                    // NAV holds canonical "/admin/..." paths; the link must stay under the
                    // console base of this address (e.g. /t/{slug}/admin), or the practice
                    // falls out of the URL (SHEET-230).
                    to={adminHref(n.to)}
                    end={n.end}
                    onClick={onCloseMobile}
                    data-testid={navTestId(n)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-xl transition ${
                        /* crm-module BEGIN C1.01 — an open section marks its CHILD as the active row, not itself too */
                        isActive && children.length === 0 ? "bg-eh-ink text-eh-bg"
                          : isActive ? "text-eh-ink font-semibold hover:bg-eh-bg"
                        /* crm-module END */
                          : "text-eh-ink/80 hover:bg-eh-bg hover:text-eh-ink"
                      }`
                    }
                  >
                    <Icon size={16} /> <span className="text-sm font-medium">{t(n.labelKey)}</span>
                    {n.to === MESSAGES_PATH && <UnreadPill count={unreadCount} />}
                  </NavLink>
                  {/* crm-module BEGIN C1.01 */}
                  {children.length > 0 && (
                    <div className="pt-1 pb-1 space-y-0.5" role="group" aria-label={t(n.labelKey)}>
                      {children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={adminHref(c.to)}
                          end={c.end}
                          onClick={onCloseMobile}
                          data-testid={navTestId(c)}
                          className={({ isActive }) =>
                            `flex items-center ml-7 px-3 py-2 rounded-xl text-sm transition ${
                              isActive ? "bg-eh-ink text-eh-bg" : "text-eh-ink/80 hover:bg-eh-bg hover:text-eh-ink"
                            }`
                          }
                        >
                          {t(c.labelKey)}
                        </NavLink>
                      ))}
                    </div>
                  )}
                  {/* crm-module END */}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          );
        })}
      </nav>

      <div className="px-4 py-4 border-t border-eh-border-soft space-y-2">
        <a href={siteUrl} target="_blank" rel="noopener noreferrer" data-testid="admin-view-site" className="flex items-center gap-2 text-xs text-eh-muted hover:text-eh-ink">
          <ExternalLink size={12} /> {t("layout.viewSite")}
        </a>
        {/* The marketplace lives on the PLATFORM host: on a tenant domain the /marketplace
            route does not exist (platformRoutes is not mounted there), so a relative link
            would render a blank page — hence the absolute origin. Without this link a
            practitioner had NO way to see their own public listing short of typing the URL
            by hand. */}
        <a href={`${platformOrigin()}/marketplace`} target="_blank" rel="noopener noreferrer" data-testid="admin-view-marketplace" className="flex items-center gap-2 text-xs text-eh-muted hover:text-eh-ink">
          <ExternalLink size={12} /> {t("layout.viewMarketplace")}
        </a>
        <div className="text-xs text-eh-muted truncate" title={user?.email}>{user?.name || user?.email}</div>
        <button
          onClick={onLogout}
          data-testid="admin-logout"
          className="w-full inline-flex items-center justify-center gap-2 text-sm px-3 py-2 rounded-full border border-eh-border-soft hover:bg-eh-ink hover:text-eh-bg transition"
        >
          <LogOut size={14} /> {tc("actions.logout")}
        </button>
      </div>
    </aside>
  );
};
